import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import type { FormControl, FormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, startWith, Subject, switchMap } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { QuestionKind } from '@core/interview-templates-types';
import {
  InputComponent,
  ProtectedControlDirective,
  TextareaComponent,
} from '@core/user-interface-legacy';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import type { QuestionBase } from '../interview-kind';

interface QuestionStringControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.String>;
  content: FormControl<string>;
}
type QuestionStringControl = FormGroup<QuestionStringControls>;

interface QuestionTextControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.Text>;
  content: FormControl<string>;
}
type QuestionTextControl = FormGroup<QuestionTextControls>;

@Component({
  selector: 'rt-interview-question',
  templateUrl: 'interview-question.component.html',
  styleUrls: ['./interview-question.component.less'],
  imports: [
    ReactiveFormsModule,
    ProtectedControlDirective,
    InputComponent,
    TextareaComponent,
    TranslatePipe,
    InterviewBaseBoxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterviewQuestionComponent implements OnChanges, OnInit, OnDestroy {
  private readonly i18n = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly formSubmissionAttempt = input<boolean>(false);
  public readonly placeholder = input<string>('app.interview.question.placeholder');
  public readonly errorMessages = input<Record<string, string>>({});
  public readonly hasProtectedControls = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });
  @Input() public questionForm: QuestionStringControl | QuestionTextControl;

  public QuestionKind: typeof QuestionKind = QuestionKind;
  public errorMessage: string | null = null;
  private questionForm$ = new BehaviorSubject<QuestionStringControl | QuestionTextControl | null>(
    null,
  );
  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges) {
    if ((changes.questionForm && this.questionForm) || changes.errorMessages) {
      this.questionForm$.next(this.questionForm);
    }
  }

  public ngOnInit() {
    this.questionForm$
      .pipe(
        filterBoolean,
        switchMap(questionForm => {
          const { content } = questionForm.controls;
          return content.statusChanges.pipe(
            startWith(null),
            map(() => content.errors),
          );
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(errors => {
        if (!errors) {
          this.errorMessage = null;
        } else {
          const [errorKey] = Object.keys(errors);
          const translationKey = this.errorMessages()[errorKey];
          const options = typeof errors[errorKey] === 'object' ? errors[errorKey] : {};
          this.errorMessage = translationKey ? this.i18n.translate(translationKey, options) : null;
        }
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
